import { Drawer, Tabs } from '@silverfort/sf-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CreateApplicationFooter from './components/CreateApplicationFooter';
import ResourceSelector from './ResourceSelector';
import { useResourceState } from './hooks/useResourceState';
import useApplicationMutations from '../../api/mutations/useApplicationMutations';
import { CreateApplicationFormInput } from './types/CreateApplicationFormInput';
import { CreateApplicationLocationState } from './types/CreateApplicationLocationState';
import { CreateApplicationDto } from './types/CreateApplicationDto';
import { CreateApplicationFormErrors } from './types/CreateApplicationFormErrors';
import { ApplicationResource, getApplicationResources } from '../../api/queries/getApplicationResources';
import { CreateApplicationForm } from './components/CreateApplicationForm';
import { usePageLayout } from '../../hooks/usePageLayout';

const TABS = [
    { name: 'Hostname', value: 1 },
    { name: 'IPs', value: 2 }
];

const CreateApplication = () => {
    const history = useHistory();
    const location = useLocation<CreateApplicationLocationState>();
    const { editMode, application } = location.state || { editMode: false, application: null };
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [tab, setTab] = useState(1);
    const [formErrors, setFormErrors] = useState<CreateApplicationFormErrors>({});
    const [appResources, setAppResources] = useState<Array<ApplicationResource> | null>(null);
    const [appResourcesLoading, setAppResourcesLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!editMode || !application?.app_id) return;

        const fetchAppResources = async () => {
            setAppResourcesLoading(true);

            try {
                const response = await getApplicationResources(application.app_id);

                setAppResources(response);
            } catch (err) {
                setAppResources(null);
            } finally {
                setAppResourcesLoading(false);
            }
        };

        fetchAppResources();
    }, [editMode, application?.app_id]);

    const [form, setForm] = useState<CreateApplicationFormInput>({
        critically: '',
        description: '',
        name: '',
        selectedHostnames: [],
        selectedIps: [],
        staleAfter: null
    });

    const { resourceState, addResource, clearResources, deleteResource } = useResourceState(
        form.selectedIps,
        form.selectedHostnames
    );

    const handleCreateApplicationSuccess = () => history.goBack();

    const { createApplicationMutation, isLoading: isCreateApplicationLoading } =
        useApplicationMutations(handleCreateApplicationSuccess);

    const { updateApplicationMutation, isLoading: isUpdatingApplicationLoading } =
        useApplicationMutations(handleCreateApplicationSuccess);

    const updateForm = useCallback(
        <K extends keyof CreateApplicationFormInput>(key: K, value: CreateApplicationFormInput[K]) => {
            setForm((prevState) => ({ ...prevState, [key]: value }));
            setFormErrors({});
        },
        []
    );

    const back = useCallback(() => {
        history.push('/AccessAnalysis');
    }, [history]);

    usePageLayout('New Application / Service', back);

    const toggleDrawer = () => setDrawerOpen((prevState) => !prevState);

    const applyResources = () => {
        setForm((prevState) => ({
            ...prevState,
            selectedIps: resourceState.selectedIps,
            selectedHostnames: resourceState.selectedHostnames
        }));
        toggleDrawer();
        setFormErrors({});
    };

    const handleSave = () => {
        if (!form.name) {
            setFormErrors({ name: 'Name is required' });
            return;
        }

        if (!form.selectedIps.length && !form.selectedHostnames.length) {
            setFormErrors({ resources: 'At least one resource is required' });
            return;
        }

        const data: CreateApplicationDto = {
            name: form.name,
            refType: 'AdminConsole',
            businessCriticality: form.critically,
            ipNames: form.selectedIps
                .map<{ hostname?: string; ipAddress?: string }>((ip) => ({ ipAddress: ip }))
                .concat(
                    form.selectedHostnames.map<{ hostname?: string; ipAddress?: string }>((hostname) => ({ hostname }))
                ),
            description: form.description,
            staleAfter: form.staleAfter
        };

        if (editMode && application) {
            updateApplicationMutation(application.app_id, data);
        } else {
            createApplicationMutation(data);
        }
    };

    useEffect(() => {
        if (!editMode || !application || appResourcesLoading) return;

        setForm({
            critically: application.business_criticality ?? '',
            description: application.description ?? '',
            name: application.name,
            selectedHostnames:
                appResources?.filter((resource) => resource.type === 'hostname').map((resource) => resource.name) ?? [],
            selectedIps:
                appResources?.filter((resource) => resource.type === 'ip')?.map((resource) => resource.name) ?? [],
            staleAfter: appResources?.map((resource) => resource.stale_after)?.at(0) ?? null
        });
    }, [appResourcesLoading, editMode, application, appResources]);

    return (
        <div
            className={`pt-[20px] pb-[30px] h-[calc(100vh-60px)] bg-sf-white relative w-[calc(100%-60px)] ml-0 top-[60px] left-[60px] flex overflow-hidden flex-col font-sf`}>
            <div className="p-6">
                <CreateApplicationForm
                    disabled={isCreateApplicationLoading || isUpdatingApplicationLoading}
                    errors={formErrors}
                    form={form}
                    toggleDrawer={toggleDrawer}
                    updateForm={updateForm}
                />
                <CreateApplicationFooter
                    onSave={handleSave}
                    onCancel={back}
                    saveText={editMode ? 'Save Changes' : 'Save'}
                    isLoading={isCreateApplicationLoading || isUpdatingApplicationLoading}
                />
            </div>
            <Drawer
                className="pt-[61px]"
                open={drawerOpen}
                anchor="right"
                footerActionTitle="Apply"
                footerCloseTitle="Cancel"
                headerTitle="Select Resources"
                onAction={applyResources}
                onClose={toggleDrawer}>
                <div className="w-[432px] flex flex-1 flex-col overflow-hidden">
                    <Tabs onClick={setTab} selectedTab={tab} tabs={TABS} className="mb-4" />
                    <ResourceSelector
                        tab={tab}
                        resourceState={resourceState}
                        addResource={addResource}
                        clearResources={clearResources}
                        deleteResource={deleteResource}
                    />
                </div>
            </Drawer>
        </div>
    );
};
export default CreateApplication;
